/**
 * Showing a repository image to a caller whose credentials live in a header.
 *
 * `pageUrl` and `thumbnailUrl` hand back a plain URL, and an `<img src>` is
 * loaded by the browser, not by the client — so it carries cookies and nothing
 * else. A PROJECT USER holds no cookie: their session is an opaque bearer
 * token the API client attaches as headers. Every page image and thumbnail
 * therefore came back 404 "Project not found" for exactly the people a running
 * application is built for, while every POST on the same screen answered 200.
 *
 * `authedBlobUrl` fetches through the client instead and returns a `blob:` URL.
 * This hook wraps that with the lifecycle it needs: the object URL is revoked
 * when the component unmounts or the source changes, or the viewer leaks one
 * copy of every page it scrolls past.
 *
 * A client without `authedBlobUrl` — the downloaded runtime, whose only caller
 * is a cookie-bearing session — falls straight through to the plain URL, so
 * nothing about that path changes.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import type { DmsClient } from './contracts'

/**
 * Whether an element has come near enough to the viewport to be worth loading.
 *
 * `loading="lazy"` on an `<img>` is the browser's own version of this, and it
 * is what the plain-URL path relies on. It does NOTHING on the path below:
 * `useAuthedSrc` fetches through the API client, which happens the moment the
 * component mounts, whether or not the element has ever been on screen. A
 * grid of a hundred cards therefore asked for a hundred previews at once for
 * exactly the callers who need the client — so the fetch is gated on the same
 * condition the browser would have used.
 *
 * Latching, deliberately: a preview scrolled past and scrolled back to must
 * not be fetched twice, and a card whose image is already decoded should not
 * blank out when it leaves the screen.
 */
export function useNearViewport(rootMargin = '300px'): {
  ref: (node: HTMLElement | null) => void
  near: boolean
} {
  // No observer (a test renderer, an old browser) means load immediately —
  // the feature degrades to what it replaced, never to a blank card.
  const [near, setNear] = useState(
    () => typeof IntersectionObserver === 'undefined')
  const observer = useRef<IntersectionObserver | null>(null)

  // A callback ref rather than an effect over an object ref: the element is
  // attached during the commit, and binding from an effect would observe a
  // node that has already been on screen for a frame.
  const ref = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect()
    observer.current = null
    if (!node || typeof IntersectionObserver === 'undefined') return
    const watch = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      setNear(true)
      watch.disconnect()
    }, { rootMargin })
    watch.observe(node)
    observer.current = watch
  }, [rootMargin])

  useEffect(() => () => observer.current?.disconnect(), [])

  return { ref, near }
}

/**
 * `src` for an `<img>`: the URL itself, or a blob standing in for it.
 *
 * Returns '' only while a blob is in flight, so a caller that renders an
 * `<img>` unconditionally shows nothing for a moment rather than firing a
 * request the server will refuse.
 */
export function useAuthedSrc(client: DmsClient, url: string): string {
  const resolve = client.authedBlobUrl
  const [src, setSrc] = useState(() => (resolve ? '' : url))

  useEffect(() => {
    if (!resolve) { setSrc(url); return undefined }
    if (!url) { setSrc(''); return undefined }
    let live = true
    let made = ''
    setSrc('')
    void resolve(url)
      .then((blobUrl) => {
        if (!live) { URL.revokeObjectURL(blobUrl); return }
        made = blobUrl
        setSrc(blobUrl)
      })
      .catch(() => {
        // Let the <img> fail on the real URL, so the surrounding component
        // reports it the way it always has ("This page could not be
        // rendered") rather than sitting blank with no explanation.
        if (live) setSrc(url)
      })
    return () => {
      live = false
      if (made) URL.revokeObjectURL(made)
    }
  }, [resolve, url])

  return src
}

/**
 * Save a repository file the caller is entitled to, credentials and all.
 *
 * The same problem as an image and the same fix, with one extra step: the blob
 * is handed to a temporary anchor so the browser saves it under a sensible
 * name. Falls back to opening the plain URL where the client cannot fetch.
 */
export async function downloadAuthed(client: DmsClient, url: string,
                                     filename: string): Promise<void> {
  const resolve = client.authedBlobUrl
  if (!resolve) {
    window.open(url, '_blank', 'noopener')
    return
  }
  const blobUrl = await resolve(url)
  try {
    const anchor = document.createElement('a')
    anchor.href = blobUrl
    anchor.download = filename || 'document'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    // After the click, not before: revoking synchronously cancels the save in
    // some browsers. A tick is enough and leaks nothing.
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000)
  }
}
